import { useMemo } from 'react';

import useAuth from 'hooks/useAuth';
import type { MenuAccess } from 'menu-items/menuVisibility';
import { useIsSageHrEnabledQuery } from 'store/api/sage-hr-integration';
import { FeatureFlag } from 'types/features';
import { SHARE_WITH_FIRM_PERMISSIONS } from 'types/homeWidgetSharing';

export type AdminItem = {
    /** For a Settings tab, the tab's id. */
    id: string;
    labelId: string;
    path: string;
    /** Where the same page opens outside `/admin`, with everything under it. */
    oldPaths: string[];
    /** All of these, as the page's route and its Settings tab require. */
    permissions: string[];
    /** Shown only while TiqTime or Sage HR is on, as its Settings tab. */
    needsIntegration?: boolean;
};

export type AdminGroup = {
    id: string;
    labelId: string;
    /** The line under the group's title on the Admin home. */
    descriptionId: string;
    items: AdminItem[];
};

export const ADMIN_ROOT = '/admin';

const SETTINGS = 'READ.BACK_OFFICE_SETTINGS';
const ROLES_AND_PERMISSIONS = 'READ.ROLES_AND_PERMISSIONS';

const settingsTab = (id: string, labelId: string, permissions = [SETTINGS]): AdminItem => ({
    id,
    labelId,
    path: `${ADMIN_ROOT}/${id}`,
    oldPaths: [`/settings/${id}`],
    permissions,
});

/** The Admin area's pages, grouped as it shows them, each behind the gate of its own route. */
export const ADMIN_GROUPS: AdminGroup[] = [
    {
        id: 'organisation',
        labelId: 'admin.group.organisation',
        descriptionId: 'admin.description.organisation',
        items: [
            {
                id: 'job-roles',
                labelId: 'menu.job-roles',
                path: '/admin/job-roles',
                oldPaths: ['/job-roles', '/job-role'],
                permissions: ['READ.JOB_ROLES'],
            },
            {
                id: 'job-positions',
                labelId: 'menu.job-positions',
                path: '/admin/job-positions',
                oldPaths: ['/job-positions', '/job-position'],
                permissions: ['READ.JOB_POSITION'],
            },
            {
                id: 'country-management',
                labelId: 'menu.country-management',
                path: '/admin/country-management',
                oldPaths: ['/country-management'],
                permissions: ['READ.BACK_OFFICE_COUNTRY_MANAGEMENT'],
            },
            {
                id: 'financials',
                labelId: 'menu.financial-conditions',
                path: '/admin/financials',
                oldPaths: ['/financial-conditions'],
                permissions: ['READ.FINANCIAL_CONDITIONS'],
            },
            {
                id: 'firm-widgets',
                labelId: 'home.widgets.firm.title',
                path: '/admin/firm-widgets',
                // The page exists under `/admin` alone.
                oldPaths: [],
                permissions: SHARE_WITH_FIRM_PERMISSIONS,
            },
        ],
    },
    {
        id: 'data-documents',
        labelId: 'admin.group.data-documents',
        descriptionId: 'admin.description.data-documents',
        items: [
            settingsTab('classifiers', 'menu.classifiers'),
            settingsTab('document-templates', 'menu.document-templates'),
            settingsTab('configuration', 'menu.configuration'),
            settingsTab('processes', 'menu.processes', ['RUN.PROCESSES']),
        ],
    },
    {
        id: 'access',
        labelId: 'admin.group.access',
        descriptionId: 'admin.description.access',
        items: [
            settingsTab('roles', 'menu.company-roles'),
            settingsTab('roles-and-permissions', 'admin.roles-and-permissions', [SETTINGS, ROLES_AND_PERMISSIONS]),
            settingsTab('feature-flags', 'menu.feature-flags'),
        ],
    },
    {
        id: 'integrations',
        labelId: 'admin.group.integrations',
        descriptionId: 'admin.description.integrations',
        items: [
            {
                ...settingsTab('data-integrations', 'menu.data-integrations', [SETTINGS, ROLES_AND_PERMISSIONS]),
                needsIntegration: true,
            },
        ],
    },
    {
        id: 'compliance-rules',
        labelId: 'admin.group.compliance-rules',
        descriptionId: 'admin.description.compliance-rules',
        items: [settingsTab('compliance', 'menu.compliance-blocking-rules')],
    },
    {
        id: 'audit-trail',
        labelId: 'audit-trail.heading',
        descriptionId: 'admin.description.audit-trail',
        items: [
            {
                id: 'audit-trail',
                labelId: 'audit-trail.heading',
                path: '/admin/audit-trail',
                oldPaths: ['/audit-trail'],
                permissions: ['READ.AUDIT_LOGS'],
            },
        ],
    },
];

export const ADMIN_ITEMS = ADMIN_GROUPS.flatMap((group) => group.items);

const DATA_INTEGRATIONS = ADMIN_ITEMS.find((item) => item.needsIntegration)!;

const matchesPath = (pathname: string, path: string) => pathname === path || pathname.startsWith(`${path}/`);

export function isAdminItemVisible(item: AdminItem, user: MenuAccess, sageHrEnabled = false): boolean {
    const hasAll = item.permissions.every((code) => user?.permissions?.some((permission) => permission.code === code));
    const integrationOn = sageHrEnabled || !!user?.enabledFeatures?.includes(FeatureFlag.INTEGRATION_TIQ);
    return hasAll && (!item.needsIntegration || integrationOn);
}

/** Whether the user may open any Admin page, and so gets the way in. */
export const isAdminVisible = (user: MenuAccess): boolean => ADMIN_ITEMS.some((item) => isAdminItemVisible(item, user));

export function visibleAdminGroups(user: MenuAccess, sageHrEnabled: boolean): AdminGroup[] {
    return ADMIN_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => isAdminItemVisible(item, user, sageHrEnabled)),
    })).filter((group) => group.items.length > 0);
}

/** The groups with the items the signed-in user may open. Sage HR is asked only when it is the last thing deciding. */
export function useVisibleAdminGroups(): AdminGroup[] {
    const { user } = useAuth();
    const { data: sageHrEnabled } = useIsSageHrEnabledQuery(undefined, {
        skip: isAdminItemVisible(DATA_INTEGRATIONS, user) || !isAdminItemVisible(DATA_INTEGRATIONS, user, true),
    });
    return useMemo(() => visibleAdminGroups(user, !!sageHrEnabled), [user, sageHrEnabled]);
}

export const isAdminItemActive = (item: AdminItem, pathname: string): boolean =>
    [item.path, ...item.oldPaths].some((path) => matchesPath(pathname, path));

/** A path under `/admin`. */
export const isAdminAreaPath = (pathname: string): boolean => matchesPath(pathname, ADMIN_ROOT);

/** An Admin page under `/admin` or under the old path it keeps; in the new design both open in the Admin area. */
export const isAdminPath = (pathname: string): boolean =>
    isAdminAreaPath(pathname) ||
    matchesPath(pathname, '/settings') ||
    ADMIN_ITEMS.some((item) => isAdminItemActive(item, pathname));

/** An Admin page is registered under `/admin` and under the old path it has; a design lists only its own. */
export function isListedInDesign(routePath: string, isNew: boolean): boolean {
    const path = routePath.startsWith('/') ? routePath : `/${routePath}`;
    return !isAdminPath(path) || isAdminAreaPath(path) === isNew;
}
